// Story 10.4 — Grid de KPIs con visibilidad role-aware.

import { Activity, AlertTriangle, MessageCircle, ShoppingBag, Timer, Users } from 'lucide-react';
import type { AnalyticsReport } from '@/lib/analytics-types';
import { formatErrorRate, formatLatencyMs } from '@/lib/period-presets';
import { KpiCard } from './KpiCard';

interface Props {
  report: AnalyticsReport;
  /** Si true (admin), muestra cards técnicas (errorRate, latencia). */
  showTechnical: boolean;
  loading?: boolean;
}

export function KpiGrid({ report, showTechnical, loading }: Props): React.ReactElement {
  const { totals, deltas } = report;
  // Reconstruimos el periodo previo a partir de current - delta.
  const prev = deltas
    ? {
        conversations: totals.conversations - deltas.conversations,
        messagesSent: totals.messagesSent - deltas.messagesSent,
        ordersCompleted: totals.ordersCompleted - deltas.ordersCompleted,
      }
    : null;

  return (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
      <KpiCard
        label="Conversaciones"
        value={totals.conversations}
        delta={deltas?.conversations}
        previous={prev?.conversations}
        icon={<Users className="h-4 w-4" aria-hidden />}
        loading={loading}
      />
      <KpiCard
        label="Mensajes recibidos"
        value={totals.messagesReceived}
        icon={<MessageCircle className="h-4 w-4" aria-hidden />}
        loading={loading}
      />
      <KpiCard
        label="Mensajes enviados"
        value={totals.messagesSent}
        delta={deltas?.messagesSent}
        previous={prev?.messagesSent}
        icon={<MessageCircle className="h-4 w-4" aria-hidden />}
        loading={loading}
      />
      <KpiCard
        label="Pedidos completados"
        value={totals.ordersCompleted}
        delta={deltas?.ordersCompleted}
        previous={prev?.ordersCompleted}
        icon={<ShoppingBag className="h-4 w-4" aria-hidden />}
        loading={loading}
      />
      {/* IG-45: "Hora pico" se muestra siempre — el spec dice que admin ve
          "todo lo anterior MÁS [técnicas]"; antes la card desaparecía
          cuando `showTechnical` se prendía. */}
      <KpiCard
        label="Hora pico (UTC)"
        value={pickPeakHour(report.hourlyDistribution)}
        icon={<Activity className="h-4 w-4" aria-hidden />}
        loading={loading}
      />
      {showTechnical && (
        <>
          <KpiCard
            label="Tasa de errores"
            value={formatErrorRate(report.errorRate)}
            icon={<AlertTriangle className="h-4 w-4" aria-hidden />}
            loading={loading}
          />
          <KpiCard
            label="Latencia mediana"
            value={formatLatencyMs(report.averageLatencyMs)}
            icon={<Timer className="h-4 w-4" aria-hidden />}
            loading={loading}
          />
        </>
      )}
    </div>
  );
}

function pickPeakHour(buckets: AnalyticsReport['hourlyDistribution']): string {
  let max = 0;
  let hour = 0;
  for (const b of buckets) {
    if (b.count > max) {
      max = b.count;
      hour = b.hour;
    }
  }
  if (max === 0) return '—';
  const next = (hour + 1) % 24;
  return `${hour}:00 – ${next}:00`;
}
